
import { StockData, StockDataPoint } from "../data/sampleData";

// Calculate moving average
export const calculateMA = (data: StockDataPoint[], period: number): StockDataPoint[] => {
  return data.map((point, index) => {
    if (index < period - 1) {
      return {
        date: point.date,
        value: null as any
      };
    }

    let sum = 0;
    for (let i = 0; i < period; i++) {
      sum += data[index - i].value;
    }

    return {
      date: point.date,
      value: Math.round((sum / period) * 100) / 100
    };
  });
};

// Calculate RSI (Relative Strength Index)
export const calculateRSI = (data: StockDataPoint[], period: number = 14): StockDataPoint[] => {
  const rsiData: StockDataPoint[] = [];
  let gains = 0;
  let losses = 0;

  // First, calculate the initial average gain and loss
  for (let i = 1; i <= period; i++) {
    const change = data[i].value - data[i - 1].value;
    if (change >= 0) {
      gains += change;
    } else {
      losses -= change;
    }
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;

  // Add first RSI value
  let rs = avgGain / avgLoss;
  let rsi = 100 - (100 / (1 + rs));
  rsiData.push({
    date: data[period].date,
    value: Math.round(rsi * 100) / 100
  });

  // Calculate RSI for remaining data points
  for (let i = period + 1; i < data.length; i++) {
    const change = data[i].value - data[i - 1].value;
    let currentGain = 0;
    let currentLoss = 0;

    if (change >= 0) {
      currentGain = change;
    } else {
      currentLoss = -change;
    }

    // Use Wilder's smoothing method
    avgGain = ((avgGain * (period - 1)) + currentGain) / period;
    avgLoss = ((avgLoss * (period - 1)) + currentLoss) / period;

    rs = avgGain / avgLoss;
    rsi = 100 - (100 / (1 + rs));

    rsiData.push({
      date: data[i].date,
      value: Math.round(rsi * 100) / 100
    });
  }

  // Add null values for the initial period where RSI isn't calculated
  for (let i = 0; i < period; i++) {
    rsiData.unshift({
      date: data[i].date,
      value: null as any
    });
  }

  return rsiData;
};

// Calculate the correlation between two data series
export const calculateCorrelation = (dataA: StockDataPoint[], dataB: StockDataPoint[]): number => {
  // Match data points by date
  const matchedPoints: {dateA: string, valueA: number, valueB: number}[] = [];
  
  // Create lookup table for faster matching
  const lookupB = new Map<string, number>();
  dataB.forEach(point => {
    lookupB.set(point.date, point.value);
  });
  
  // Find matching dates
  dataA.forEach(pointA => {
    if (lookupB.has(pointA.date)) {
      matchedPoints.push({
        dateA: pointA.date,
        valueA: pointA.value,
        valueB: lookupB.get(pointA.date)!
      });
    }
  });
  
  if (matchedPoints.length < 2) return 0;
  
  // Calculate correlation
  const n = matchedPoints.length;
  
  // Calculate means
  const meanA = matchedPoints.reduce((sum, point) => sum + point.valueA, 0) / n;
  const meanB = matchedPoints.reduce((sum, point) => sum + point.valueB, 0) / n;
  
  // Calculate covariance and variance
  let covariance = 0;
  let varA = 0;
  let varB = 0;
  
  matchedPoints.forEach(point => {
    const diffA = point.valueA - meanA;
    const diffB = point.valueB - meanB;
    covariance += diffA * diffB;
    varA += diffA * diffA;
    varB += diffB * diffB;
  });
  
  covariance /= n;
  varA /= n;
  varB /= n;
  
  // Calculate correlation
  const stdA = Math.sqrt(varA);
  const stdB = Math.sqrt(varB);
  
  if (stdA === 0 || stdB === 0) return 0;
  
  return covariance / (stdA * stdB);
};

// Simple LSTM prediction simulation
export const simulateLSTMPrediction = (data: StockDataPoint[], days: number = 30): StockDataPoint[] => {
  const predictions: StockDataPoint[] = [];
  
  // Get the last date and value from the data
  const lastDataPoint = data[data.length - 1];
  let currentValue = lastDataPoint.value;
  const lastDate = new Date(lastDataPoint.date);
  
  // Generate prediction data
  for (let i = 1; i <= days; i++) {
    const predictionDate = new Date(lastDate);
    predictionDate.setDate(predictionDate.getDate() + i);
    
    // Skip weekends
    if (predictionDate.getDay() === 0 || predictionDate.getDay() === 6) {
      continue;
    }
    
    // Generate prediction with slight upward bias and some volatility
    // This is just a simulation - real LSTM would be much more complex
    const randomWalk = (Math.random() - 0.48) * 0.01 * currentValue;
    const trend = 0.0005 * currentValue; // slight upward bias
    
    // Update the current value with the random walk and trend components
    currentValue = currentValue + randomWalk + trend;
    
    predictions.push({
      date: predictionDate.toISOString().split('T')[0],
      value: Math.round(currentValue * 100) / 100
    });
  }
  
  return predictions;
};

// Format percentage change with + or - sign
export const formatPercentChange = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// Format currency value
export const formatCurrency = (value: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency, 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(value);
};

// Format large numbers with K, M, B suffixes
export const formatLargeNumber = (num: number): string => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Convert date string to formatted date
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Extract min and max values from data series
export const getDataRange = (data: StockDataPoint[]): { min: number, max: number } => {
  const values = data.map(d => d.value).filter(v => v !== null && !isNaN(v)) as number[];
  return {
    min: Math.min(...values),
    max: Math.max(...values)
  };
};
